import { escapeAttribute, escapeScriptClose, escapeStyleClose } from './escape.js';

const CONSOLE_RELAY_SCRIPT = `
        (function() {
            const MAX_TEXT = 6000;
            const postToPreviewRelay = (message) => {
                // The srcdoc sandbox has an opaque origin and some browsers
                // expose the top app origin, not the immediate relay origin, as
                // document.referrer. The target window is still the preview
                // relay, which validates and forwards only known message types.
                window.parent.postMessage(message, '*');
            };

            const clip = (value) => {
                const text = String(value);
                return text.length > MAX_TEXT ? text.slice(0, MAX_TEXT) + "\\n... [truncated]" : text;
            };

            const describeElement = (el) => {
                const tag = el.tagName ? el.tagName.toLowerCase() : 'element';
                const id = el.id ? '#' + el.id : '';
                const classes = el.className && typeof el.className === 'string'
                    ? '.' + el.className.trim().split(/\\s+/).filter(Boolean).slice(0, 3).join('.')
                    : '';
                return '<' + tag + id + classes + '>';
            };

            const serializeArg = (arg) => {
                if (arg === null) return { type: 'null', preview: 'null' };
                if (arg === undefined) return { type: 'undefined', preview: 'undefined' };
                if (typeof arg === 'string') return { type: 'string', preview: arg };
                if (typeof arg === 'number' || typeof arg === 'boolean') {
                    return { type: typeof arg, preview: String(arg) };
                }
                if (typeof arg === 'bigint') return { type: 'bigint', preview: String(arg) + 'n' };
                if (typeof arg === 'symbol') return { type: 'symbol', preview: String(arg) };
                if (typeof arg === 'function') {
                    return { type: 'function', preview: '[Function ' + (arg.name || 'anonymous') + ']' };
                }
                if (arg instanceof Error) {
                    return {
                        type: 'error',
                        preview: (arg.name || 'Error') + ': ' + (arg.message || ''),
                        stack: arg.stack || ''
                    };
                }
                if (arg instanceof Element) {
                    return { type: 'element', preview: describeElement(arg) };
                }

                const seen = new WeakSet();
                try {
                    const json = JSON.stringify(arg, (key, value) => {
                        if (typeof value === 'bigint') return String(value) + 'n';
                        if (typeof value === 'function') return '[Function ' + (value.name || 'anonymous') + ']';
                        if (value instanceof Error) return { name: value.name, message: value.message, stack: value.stack };
                        if (value instanceof Element) return describeElement(value);
                        if (value && typeof value === 'object') {
                            if (seen.has(value)) return '[Circular]';
                            seen.add(value);
                        }
                        return value;
                    }, 2);
                    return { type: Array.isArray(arg) ? 'array' : 'object', preview: clip(json) };
                } catch (err) {
                    return { type: 'object', preview: Object.prototype.toString.call(arg) };
                }
            };

            const userLocation = (lineno, colno) => {
                let line = lineno;
                try {
                    const userScript = document.querySelector('script[data-front-my-end-user-script="true"]');
                    if (userScript) {
                        const html = document.documentElement.outerHTML;
                        const scriptIndex = html.indexOf(userScript.innerHTML);
                        if (scriptIndex !== -1) {
                            const beforeScript = html.slice(0, scriptIndex);
                            const offset = (beforeScript.match(/\\n/g) || []).length;
                            if (lineno > offset) line = Math.max(1, lineno - offset - 1);
                        }
                    }
                } catch (err) {}
                return { line, column: colno || 0 };
            };

            const sendLog = (level, args, meta) => {
                const entries = Array.from(args).map(serializeArg);
                const message = entries.map((entry) => entry.preview).join(' ');
                const stack = entries.find((entry) => entry.stack)?.stack || (meta && meta.stack) || '';
                postToPreviewRelay({
                    type: 'console',
                    source: 'Preview',
                    level: level === 'log' || level === 'debug' ? 'info' : level,
                    message,
                    entries,
                    stack,
                    line: meta && meta.line,
                    column: meta && meta.column
                });
            };
            ['log', 'info', 'warn', 'error', 'debug'].forEach((name) => {
                const orig = console[name];
                console[name] = (...args) => {
                    sendLog(name, args);
                    if (orig) orig.apply(console, args);
                };
            });

            window.onerror = (message, source, lineno, colno, error) => {
                const loc = userLocation(lineno || 1, colno || 0);
                sendLog('error', [error || message], {
                    line: loc.line,
                    column: loc.column,
                    stack: error && error.stack ? error.stack : ''
                });
                return false;
            };

            window.addEventListener('unhandledrejection', (event) => {
                const reason = event.reason || 'Unhandled promise rejection';
                sendLog('error', [reason], { stack: reason && reason.stack ? reason.stack : '' });
            });

            window.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                postToPreviewRelay({ type: 'preview-contextmenu', clientX: e.clientX, clientY: e.clientY });
            });
        })();
`;

const PREVIEW_RUNTIME_SCRIPT = `
        (function() {
            const runtimeScript = document.currentScript;
            const activeRoute = runtimeScript ? runtimeScript.dataset.activeRoute || '' : '';
            const activePath = runtimeScript ? runtimeScript.dataset.activePath || 'index.html' : 'index.html';
            const postToPreviewRelay = (message) => {
                // The srcdoc sandbox has an opaque origin and some browsers
                // expose the top app origin, not the immediate relay origin, as
                // document.referrer. The target window is still the preview
                // relay, which validates and forwards only known message types.
                window.parent.postMessage(message, '*');
            };

            // Scroll preservation for implicit re-renders (Live HTML edits,
            // theme switches). The editor keeps the last reported position and
            // embeds it on the next document; explicit Runs start at the top.
            const restoreScrollRaw = runtimeScript ? runtimeScript.dataset.restoreScroll || '' : '';
            if (restoreScrollRaw) {
                const parts = restoreScrollRaw.split(',').map(Number);
                const restoreX = Number.isFinite(parts[0]) ? parts[0] : 0;
                const restoreY = Number.isFinite(parts[1]) ? parts[1] : 0;
                const restoreScroll = () => {
                    window.requestAnimationFrame(() => {
                        try { window.scrollTo(restoreX, restoreY); } catch (err) {}
                    });
                };
                if (document.readyState === 'complete') {
                    restoreScroll();
                } else {
                    window.addEventListener('load', restoreScroll, { once: true });
                }
            }

            let scrollReportTimer = null;
            window.addEventListener('scroll', () => {
                if (scrollReportTimer) return;
                scrollReportTimer = window.setTimeout(() => {
                    scrollReportTimer = null;
                    postToPreviewRelay({ type: 'preview-scroll', x: window.scrollX || 0, y: window.scrollY || 0 });
                }, 200);
            }, { passive: true });

            const hasExternalScheme = (href) => /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('//');
            const controlMessageTypes = new Set(['style-update', 'front-my-end-inspect-mode']);

            const asElement = (target) => {
                if (!target) return null;
                if (target.nodeType === Node.ELEMENT_NODE) return target;
                return target.parentElement || null;
            };

            const closestFromEvent = (event, selector) => {
                const element = asElement(event.target);
                return element && typeof element.closest === 'function'
                    ? element.closest(selector)
                    : null;
            };

            const inspectTargetFromEvent = (event) => {
                let element = null;
                if (typeof document.elementFromPoint === 'function' && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
                    element = document.elementFromPoint(event.clientX, event.clientY);
                }
                return asElement(element || event.target);
            };

            const sendNavigationRequest = (href) => {
                postToPreviewRelay({
                    type: 'preview-navigate',
                    href,
                    fromPath: activePath
                });
            };

            const dispatchHashChange = (oldURL, newURL) => {
                try {
                    window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL, newURL }));
                } catch (err) {
                    window.dispatchEvent(new Event('hashchange'));
                }
            };

            const applyHashRoute = (href, mode) => {
                const hash = typeof href === 'string' && href.startsWith('#') ? href : '';
                if (!hash) return false;

                const oldURL = window.location.href;
                let routeApplied = false;
                try {
                    const method = mode === 'replace' ? 'replaceState' : 'pushState';
                    history[method](null, '', hash);
                    routeApplied = true;
                } catch (err) {
                    try {
                        history.replaceState(null, '', hash);
                        routeApplied = true;
                    } catch (err2) {}
                }

                // If history API failed due to WebKit srcdoc SecurityError, fallback to location.hash
                if (!routeApplied) {
                    try {
                        if (window.location.hash !== hash) {
                            window.location.hash = hash;
                            routeApplied = true;
                        } else {
                            routeApplied = true;
                        }
                    } catch (err3) {}
                }

                const newURL = routeApplied
                    ? window.location.href
                    : oldURL.replace(/#.*$/, '') + hash;
                dispatchHashChange(oldURL, newURL);
                return routeApplied;
            };

            const notifyActiveRoute = () => {
                const currentHash = window.location.hash || '';
                postToPreviewRelay({ type: 'active-route', hash: currentHash });
            };

            window.addEventListener('hashchange', notifyActiveRoute);
            window.addEventListener('popstate', notifyActiveRoute);

            document.addEventListener('click', (e) => {
                const anchor = closestFromEvent(e, 'a');
                if (!anchor) return;

                const href = anchor.getAttribute('href');
                if (!href) return;

                e.preventDefault();
                if (href.startsWith('#')) {
                    applyHashRoute(href, 'push');

                    console.log('[Interaction] Clicked: <a href="' + href + '">');
                    postToPreviewRelay({ type: 'active-route', hash: href });
                } else if (!hasExternalScheme(href)) {
                    console.log('[Navigation] Requested preview page: ' + href);
                    sendNavigationRequest(href);
                } else {
                    console.error("[Navigation Error] Blocked navigation to: " + href + ". External navigation is disabled in live preview mode. Use Standalone Export to test external links.");
                }
            });

            if (activeRoute && activeRoute !== window.location.hash) {
                const syncInitialRoute = () => {
                    if (activeRoute && activeRoute !== window.location.hash) {
                        applyHashRoute(activeRoute, 'replace');
                    }
                };
                if (document.readyState === 'loading') {
                    window.addEventListener('DOMContentLoaded', syncInitialRoute, { once: true });
                } else {
                    syncInitialRoute();
                }
            }

            window.addEventListener('click', (e) => {
                const t = closestFromEvent(e, 'button, a, input, select, canvas, summary');
                if (t) {
                    const tag = t.tagName.toLowerCase();
                    const desc = t.id ? '#' + t.id : (t.className && typeof t.className === 'string' ? '.' + t.className.trim().split(/\\s+/)[0] : '');
                    const text = t.textContent ? ' "' + t.textContent.trim().slice(0, 20) + '"' : '';
                    console.log("[Interaction] Clicked: <" + tag + desc + text + ">");
                }
            }, true);

            window.addEventListener('input', (e) => {
                const t = e.target;
                if (t.tagName === 'INPUT') {
                    console.log("[Interaction] Input: <input#" + (t.id || 'slider') + ' value="' + t.value + '">');
                }
            }, true);

            window.addEventListener('change', (e) => {
                const t = e.target;
                if (t.tagName === 'SELECT') {
                    console.log("[Interaction] Change: <select#" + (t.id || 'select') + ' selected="' + t.value + '">');
                }
            }, true);

            let inspectEnabled = false;
            let inspectBox = null;
            let inspectedElement = null;

            const ensureInspectBox = () => {
                if (inspectBox) return inspectBox;
                inspectBox = document.createElement('div');
                inspectBox.style.position = 'fixed';
                inspectBox.style.pointerEvents = 'none';
                inspectBox.style.zIndex = '2147483647';
                inspectBox.style.border = '3px solid #ff5f1f';
                inspectBox.style.boxShadow = '0 0 0 2px rgba(39, 24, 19, 0.75)';
                inspectBox.style.background = 'rgba(255, 95, 31, 0.12)';
                inspectBox.style.display = 'none';
                document.documentElement.appendChild(inspectBox);
                return inspectBox;
            };

            const drawInspectBox = (element) => {
                const box = ensureInspectBox();
                if (!element || element === document.documentElement || element === document.body) {
                    box.style.display = 'none';
                    return;
                }
                const rect = element.getBoundingClientRect();
                box.style.display = 'block';
                box.style.left = rect.left + 'px';
                box.style.top = rect.top + 'px';
                box.style.width = rect.width + 'px';
                box.style.height = rect.height + 'px';
            };

            const matchingCssSelectors = (element) => {
                if (!element || typeof element.matches !== 'function') return [];
                const selectors = [];
                Array.from(document.styleSheets).forEach((sheet) => {
                    let rules = [];
                    try {
                        rules = Array.from(sheet.cssRules || []);
                    } catch (err) {
                        return;
                    }
                    rules.forEach((rule) => {
                        if (!rule.selectorText) return;
                        const parts = rule.selectorText.split(',').map((part) => part.trim()).filter(Boolean);
                        parts.forEach((selector) => {
                            try {
                                if (element.matches(selector)) selectors.push(selector);
                            } catch (err) {}
                        });
                    });
                });
                return Array.from(new Set(selectors)).slice(0, 8);
            };

            const sendInspectResult = (element) => {
                if (!element) return;
                const classList = Array.from(element.classList || []);
                postToPreviewRelay({
                    type: 'front-my-end-inspect-result',
                    tagName: element.tagName || '',
                    id: element.id || '',
                    classes: classList,
                    cssSelectors: matchingCssSelectors(element)
                });
            };

            const onInspectMove = (event) => {
                if (!inspectEnabled) return;
                inspectedElement = inspectTargetFromEvent(event);
                drawInspectBox(inspectedElement);
            };

            const onInspectClick = (event) => {
                if (!inspectEnabled) return;
                event.preventDefault();
                event.stopPropagation();
                inspectedElement = inspectTargetFromEvent(event);
                drawInspectBox(inspectedElement);
                sendInspectResult(inspectedElement);
            };

            window.addEventListener('message', (event) => {
                const data = event.data;
                if (!data || !controlMessageTypes.has(data.type)) return;
                // WebKit can report a null source for messages into opaque
                // sandboxed srcdoc frames. The outer sandbox relay still
                // validates parent origin and message shape before forwarding.
                if (event.source && event.source !== window.parent) return;
                if (data && data.type === 'style-update') {
                    const userStyle = document.getElementById('user-styles');
                    if (userStyle) {
                        userStyle.textContent = data.css || '';
                    }
                    return;
                }
                inspectEnabled = Boolean(data.enabled);
                document.documentElement.style.cursor = inspectEnabled ? 'crosshair' : '';
                if (!inspectEnabled && inspectBox) inspectBox.style.display = 'none';
            });

            window.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    postToPreviewRelay({ type: 'preview-escape' });
                }
            });

            document.addEventListener('pointermove', onInspectMove, true);
            document.addEventListener('mousemove', onInspectMove, true);
            document.addEventListener('click', onInspectClick, true);
        })();
`;

const INTERACTION_LOGGER_SCRIPT = `  <script>
    (function() {
      console.log("%cStandalone Interaction Logger Active", "color: #ff5f1f; font-weight: bold; font-size: 13px;");

      const logInteraction = (type, details) => {
        console.log(\`[Interaction] \${type}:\`, details);
      };

      window.addEventListener('click', (e) => {
        const t = e.target.closest('button, a, input, select, canvas, summary');
        if (t) {
          const tag = t.tagName.toLowerCase();
          const desc = t.id ? '#' + t.id : (t.className && typeof t.className === 'string' ? '.' + t.className.trim().split(/\\s+/)[0] : '');
          const text = t.textContent ? ' "' + t.textContent.trim().slice(0, 20) + '"' : '';
          logInteraction('Click', \`<\${tag}\${desc}\${text}>\`);
        }
      }, true);

      window.addEventListener('input', (e) => {
        const t = e.target;
        if (t.tagName === 'INPUT') {
          logInteraction('Input', \`<input#\${t.id || 'slider'} value="\${t.value}">\`);
        }
      }, true);

      window.addEventListener('change', (e) => {
        const t = e.target;
        if (t.tagName === 'SELECT') {
          logInteraction('Change', \`<select#\${t.id || 'select'} selected="\${t.value}">\`);
        }
      }, true);
    })();
  </script>`;

const ROUTE_BOOTSTRAP_SCRIPT = `
    (function() {
      try {
        const script = document.currentScript;
        const initialRoute = script ? script.dataset.activeRoute : '';
        if (initialRoute && initialRoute.startsWith('#') && window.location.hash !== initialRoute) {
          try {
            history.replaceState(null, '', initialRoute);
          } catch (e1) {
            try {
              window.location.hash = initialRoute;
            } catch (e2) {}
          }
        }
      } catch (err) {}
    })();
`;

function formatRestoreScrollAttribute(restoreScroll) {
  if (!restoreScroll) return '';
  const x = Number.isFinite(restoreScroll.x) && restoreScroll.x > 0 ? Math.round(restoreScroll.x) : 0;
  const y = Number.isFinite(restoreScroll.y) && restoreScroll.y > 0 ? Math.round(restoreScroll.y) : 0;
  if (!x && !y) return '';
  return ` data-restore-scroll="${x},${y}"`;
}

export function compilePreviewDocument(html, css, js, themeCss, activeRoute = '', activePath = 'index.html', options = {}) {
  const normalizedActivePath = activePath || 'index.html';
  const activeRouteAttribute = activeRoute
    ? ` data-active-route="${escapeAttribute(activeRoute)}"`
    : '';
  const activePathAttribute = ` data-active-path="${escapeAttribute(normalizedActivePath)}"`;
  const restoreScrollAttribute = formatRestoreScrollAttribute(options.restoreScroll);
  const safeCss = escapeStyleClose(css);
  const safeJs = escapeScriptClose(js);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <base href="about:srcdoc">
  <style id="theme-styles">
    html, body {
      background-color: #ffffff;
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
    }
${themeCss}
  </style>
  <style id="user-styles">
${safeCss}
  </style>
</head>
<body>
${html}
  <script>
${CONSOLE_RELAY_SCRIPT}
  </script>
  <script${activeRouteAttribute}>
${ROUTE_BOOTSTRAP_SCRIPT}
  </script>
  <script data-front-my-end-user-script="true">
    try {
${safeJs}
    } catch (err) {
      console.error(err);
    }
  </script>
  <script${activeRouteAttribute}${activePathAttribute}${restoreScrollAttribute}>
${PREVIEW_RUNTIME_SCRIPT}
  </script>
</body>
</html>`;
}

export function buildStandaloneDocument(html, css, js) {
  const safeCss = escapeStyleClose(css);
  const safeJs = escapeScriptClose(js);
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Front-my-End Standalone Export</title>
  <style>
    html, body {
      background-color: #ffffff;
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
    }
${safeCss}
  </style>
</head>
<body>
${html}
${INTERACTION_LOGGER_SCRIPT}
  <script>
    try {
${safeJs}
    } catch (err) {
      console.error("Runtime Exception:", err);
    }
  </script>
</body>
</html>`;
}
